// src/contexts/SocketContext.tsx
// React context that manages the Socket.IO connection lifecycle.
// Equivalent to the legacy SocketIOService + $rootScope.$broadcast('updateSocketConnectionId').

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { socketService, type SocketInstance } from '../services/socketService';
import { config } from '../config/env';
import { authStorage } from '../lib/storage';
import { useAuth } from './AuthContext';

interface SocketContextValue {
  socket: SocketInstance | null;
  connectionId: string;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connectionId: '',
});

export const useSocket = (): SocketContextValue => useContext(SocketContext);

/** Returns the current socket connection ID. Equivalent to SocketIOService.connectionId. */
export const useSocketConnectionId = (): string => useContext(SocketContext).connectionId;

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) =>
{
  const [connectionId, setConnectionId] = useState<string>('');
  const [socket, setSocket] = useState<SocketInstance | null>(null);
  const hasConnected = useRef(false);
  const { isAuthenticated } = useAuth();

  useEffect(() =>
  {
    if (hasConnected.current) return;

    const accessToken = authStorage.getAccessToken();
    const personId = authStorage.getPersonId();

    if (!accessToken || !personId) return;

    hasConnected.current = true;

    const tenancyName =
      (authStorage as any).getTenancyName?.() ||
      localStorage.getItem('C2_TenancyName') ||
      config.tenant.defaultName;

    socketService.setOnConnectionIdChange(id =>
    {
      setConnectionId(id);
    });

    const sock = socketService.connect(config.socket.server, {
      PersonId: parseInt(personId, 10),
      TenancyName: tenancyName,
      Platform: 'web',
      Token: accessToken,
      Muted: false,
    });

    setSocket(sock);

    // No cleanup - socket persists for entire app lifecycle
    // Prevents reconnections from React StrictMode double-mounting in dev
    // Socket will disconnect when user closes tab/navigates away (browser handles it)
  }, [isAuthenticated]);

  return (
    <SocketContext.Provider value={{ socket, connectionId }}>{children}</SocketContext.Provider>
  );
};
